import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { emailAPI } from '../utils/api';
import { Plus, Edit, Trash2, Save, X, Eye, FileText } from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_TEMPLATE_TYPES = [
  { type: 'client_welcome', label: 'Client Welcome', description: 'Sent when a new client account is created' },
  { type: 'agreement_sent', label: 'Agreement Sent', description: 'Notification when agreement is ready for signing' },
  { type: 'agreement_signed', label: 'Agreement Signed', description: 'Confirmation after agreement is signed' },
  { type: 'document_rejected', label: 'Document Rejected', description: 'Notify client when document needs resubmission' },
  { type: 'pending_document_reminder', label: 'Document Reminder', description: 'Daily reminder for pending documents' },
  { type: 'visa_decision_received', label: 'Visa Decision', description: 'Notification when visa decision is received' },
  { type: 'officer_new_case_assigned', label: 'New Case (Officer)', description: 'Notify officer of new case assignment' },
  { type: 'officer_document_uploaded', label: 'Document Upload (Officer)', description: 'Notify officer when client uploads document' },
];

const AVAILABLE_VARIABLES = [
  '{{Client_Name}}',
  '{{Client_Email}}',
  '{{Visa_Type}}',
  '{{Country}}',
  '{{Case_Officer_Name}}',
  '{{Portal_Link}}',
  '{{Temp_Password}}',
  '{{Document_Name}}',
  '{{Rejection_Reason}}',
  '{{Days_Remaining}}',
  '{{Missing_Documents_List}}',
  '{{Decision}}',
  '{{Officer_Name}}',
  '{{Case_Stage}}',
];

export default function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [formData, setFormData] = useState({
    type: '',
    subject: '',
    body_html: '',
    description: '',
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await emailAPI.getTemplates();
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template.type);
    setFormData({
      type: template.type,
      subject: template.subject,
      body_html: template.body_html,
      description: template.description || '',
    });
  };

  const handleCreate = (templateType) => {
    const defaultInfo = DEFAULT_TEMPLATE_TYPES.find(t => t.type === templateType);
    setEditingTemplate('new');
    setFormData({
      type: templateType || '',
      subject: '',
      body_html: '',
      description: defaultInfo?.description || '',
    });
  };

  const handleSave = async () => {
    try {
      await emailAPI.saveTemplate(formData);
      toast.success('Template saved successfully');
      setEditingTemplate(null);
      setFormData({ type: '', subject: '', body_html: '', description: '' });
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleDelete = async (templateType) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await emailAPI.deleteTemplate(templateType);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleCancel = () => {
    setEditingTemplate(null);
    setFormData({ type: '', subject: '', body_html: '', description: '' });
  };

  const insertVariable = (variable) => {
    setFormData(prev => ({
      ...prev,
      body_html: prev.body_html + variable,
    }));
  };

  const getTemplateByType = (type) => templates.find(t => t.type === type);

  // Edit/Create Template View
  if (editingTemplate) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <div className="main-content" data-testid="email-templates-edit">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                {editingTemplate === 'new' ? 'Create Email Template' : 'Edit Email Template'}
              </h1>
              <p className="text-slate-500 text-sm">Configure email content and variables</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-all flex items-center gap-2"
              >
                <X size={18} />
                Cancel
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Template Type</label>
                  <select
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900 outline-none disabled:bg-slate-50 disabled:text-slate-500"
                    value={formData.type}
                    onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                    disabled={editingTemplate !== 'new'}
                  >
                    <option value="">Select Type</option>
                    {DEFAULT_TEMPLATE_TYPES.map(t => (
                      <option key={t.type} value={t.type}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Subject Line</label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Email subject (supports variables like {{Client_Name}})"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Template description for admin reference"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email Body (HTML)</label>
                  <textarea
                    className="w-full h-64 px-4 py-2 border border-slate-200 rounded-lg font-mono text-sm focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900 outline-none"
                    value={formData.body_html}
                    onChange={(e) => setFormData(prev => ({ ...prev, body_html: e.target.value }))}
                    placeholder="<html><body>Your email content here...</body></html>"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={handleSave}
                    disabled={!formData.type || !formData.subject || !formData.body_html}
                    className="px-6 py-2.5 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-all disabled:opacity-50 flex items-center gap-2"
                  >
                    <Save size={18} />
                    Save Template
                  </button>
                  <button
                    onClick={() => setPreviewTemplate(formData)}
                    className="px-6 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-all flex items-center gap-2"
                  >
                    <Eye size={18} />
                    Preview
                  </button>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <h3 className="font-semibold text-slate-900 mb-2">Available Variables</h3>
                <p className="text-xs text-slate-500 mb-3">Click to insert into email body</p>
                <div className="flex flex-wrap gap-2">
                  {AVAILABLE_VARIABLES.map(variable => (
                    <button
                      key={variable}
                      onClick={() => insertVariable(variable)}
                      className="px-2 py-1 bg-slate-100 text-slate-700 rounded text-xs font-mono hover:bg-slate-200 transition-all"
                    >
                      {variable}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <h3 className="font-semibold text-slate-900 mb-2">Tips</h3>
                <ul className="text-sm text-slate-600 space-y-1.5">
                  <li>• Use HTML for styling (supports inline CSS)</li>
                  <li>• Variables will be replaced with actual values</li>
                  <li>• Include {"{{Portal_Link}}"} for client access</li>
                  <li>• Keep subject lines under 60 characters</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Preview Modal */}
          {previewTemplate && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl w-full max-w-2xl max-h-[80vh] overflow-auto m-4">
                <div className="p-4 border-b border-slate-200 flex justify-between items-center">
                  <h3 className="font-semibold text-slate-900">Email Preview</h3>
                  <button onClick={() => setPreviewTemplate(null)} className="p-2 hover:bg-slate-100 rounded-lg">
                    <X size={18} />
                  </button>
                </div>
                <div className="p-4">
                  <div className="mb-4">
                    <span className="font-medium text-slate-700">Subject:</span>{' '}
                    <span className="text-slate-600">{previewTemplate.subject}</span>
                  </div>
                  <div 
                    className="border border-slate-200 rounded-lg p-4"
                    dangerouslySetInnerHTML={{ __html: previewTemplate.body_html }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Template List View
  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="main-content" data-testid="email-templates-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Email Templates</h1>
            <p className="text-slate-500 text-sm">Customize email content for different notifications</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleCreate('')}
              className="px-4 py-2 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-all flex items-center gap-2"
            >
              <Plus size={18} />
              Create Template
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-slate-300 border-t-slate-800 rounded-full"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {DEFAULT_TEMPLATE_TYPES.map(templateDef => {
              const template = getTemplateByType(templateDef.type);
              
              return (
                <div key={templateDef.type} className="bg-white rounded-xl border border-slate-200 p-5">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText size={18} className="text-slate-500" />
                        <h3 className="font-semibold text-slate-900">{templateDef.label}</h3>
                        {template ? (
                          <span className="px-2.5 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                            Configured
                          </span>
                        ) : (
                          <span className="px-2.5 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
                            Using Default
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mb-2">{templateDef.description}</p>
                      {template && (
                        <div className="text-sm">
                          <span className="font-medium text-slate-700">Subject:</span>{' '}
                          <span className="text-slate-600">{template.subject}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {template ? (
                        <>
                          <button
                            onClick={() => handleEdit(template)}
                            className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition-all flex items-center gap-1"
                          >
                            <Edit size={16} />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(template.type)}
                            className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-all"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleCreate(templateDef.type)}
                          className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition-all flex items-center gap-1"
                        >
                          <Plus size={16} />
                          Customize
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
